import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles, getHotArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) return <Navigate to="/" replace />;

  const article = getArticleBySlug(slug);

  if (!article) return <Navigate to="/" replace />;

  const relatedArticles = getRelatedArticles(article.slug, article.category, 4);
  const hotArticles = getHotArticles().filter(a => a.slug !== article.slug).slice(0, 5);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '51黑料',
      url: 'https://51heiliao.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://51heiliao.com/article/${article.slug}`,
    },
    articleSection: article.categoryName,
    keywords: article.tags.join(','),
    wordCount: article.content.length,
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`51黑料,${article.categoryName},${article.tags.join(',')},51黑料爆料`}
        canonicalUrl={`https://51heiliao.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
        articleTags={article.tags}
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <article className="flex-1 min-w-0">
              {/* Breadcrumb */}
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: article.categoryName, href: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
              </div>

              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap hover:bg-primary-100 transition-colors"
                  >
                    {article.categoryName}
                  </Link>
                  {article.isHot && (
                    <span className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                      <i className="ri-fire-line mr-1"></i>热门
                    </span>
                  )}
                </div>
                <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="aspect-[21/9] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200/70">
                <span className="text-sm text-foreground-500 mr-2">
                  <i className="ri-price-tag-3-line"></i> 标签：
                </span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${article.category}`}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              {/* Share */}
              <div className="flex items-center gap-3 mt-6">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors">
                  <i className="ri-link"></i>
                </button>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section className="mt-12 pt-8 border-t border-background-200/70">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                    <i className="ri-links-line text-primary-500"></i>相关推荐
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.slug}`}
                        className="flex items-start gap-3 p-3 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors group"
                      >
                        <div className="w-24 h-16 flex-shrink-0 rounded overflow-hidden">
                          <img src={rel.image} alt={rel.title} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                            {rel.title}
                          </h3>
                          <span className="text-xs text-foreground-400 mt-1 block">{rel.date}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>

            {/* Sidebar */}
            <aside className="w-full lg:w-[320px] flex-shrink-0">
              <div className="sticky top-20 space-y-6">
                {/* Hot Articles */}
                <div className="bg-background-100 rounded-lg border border-background-200/70 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500"></i>热门爆料
                  </h3>
                  <div className="space-y-1">
                    {hotArticles.map((hot, index) => (
                      <Link
                        key={hot.id}
                        to={`/article/${hot.slug}`}
                        className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-background-200/50 transition-colors group"
                      >
                        <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                          index < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-300 text-foreground-600'
                        }`}>
                          {index + 1}
                        </span>
                        <p className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {hot.title}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Categories */}
                <div className="bg-background-100 rounded-lg border border-background-200/70 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">
                    <i className="ri-grid-line text-primary-500 mr-2"></i>分类导航
                  </h3>
                  <div className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          cat.slug === article.category
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:bg-background-200/70'
                        }`}
                      >
                        <i className={cat.icon}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}